import { useEffect, useMemo, useRef, useState } from "react";
import { deriveProfileKeys } from "@/lib/profileKeys";
import { Link } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import ProfileNudge from "@/components/ProfileNudge";
import SearchProgress from "@/components/SearchProgress";
import EmptyAdvice from "@/components/EmptyAdvice";
import DeliveryMode from "@/components/DeliveryMode";
import api, { apiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { TYPE_META, CHANNEL_META, typeLabel, LEAD_LABEL } from "@/lib/format";
import {
  Radar, Loader2, ExternalLink, Save, X, Users, Info, Instagram,
  CheckCircle2, Sparkles, Globe, MousePointerClick, ChevronDown, ChevronRight, Search,
} from "lucide-react";

const OBJECTIVES = [
  { k: "cliente", label: "Cliente" },
  { k: "socio", label: "Socio / distribuidor" },
  { k: "afiliado", label: "Colaborador / afiliado" },
  { k: "recomendador", label: "Recomendador" },
  { k: "seguidor", label: "Seguidor cualificado" },
  { k: "equipo", label: "Crear equipo" },
];

const splitList = (s) => (s || "").split(",").map((k) => k.trim()).filter(Boolean);

const GEO_LABEL = {
  CONFIRMED_COMPATIBLE: "compatible", CONFIRMED_INCOMPATIBLE: "fuera de tu mercado", UNKNOWN: "sin confirmar",
  match: "compatible", wrong: "fuera de tu mercado", unknown: "sin confirmar", unrestricted: "sin restricción",
};
const CLASS_TONE = {
  buena_oportunidad: "border-brand-pos/40 bg-brand-pos/[0.12] text-brand-pos",
  colaboracion: "border-brand-accent/40 bg-brand-accent/[0.10] text-brand-accent",
  cultivar: "border-brand-silver/30 bg-brand-silver/[0.10] text-brand-silver",
  seguidor_cualificado: "border-brand-silver/30 bg-brand-silver/[0.10] text-brand-silver",
  no_priorizar: "border-brand-line bg-brand-panel2 text-brand-muted",
};

// Etiqueta coherente con la cualificación: la prioridad la decide el análisis del perfil, no el tipo de objetivo.
const PRIO_META = {
  HIGH: { label: "Prioridad alta", tone: "border-brand-pos/40 bg-brand-pos/[0.12] text-brand-pos" },
  MEDIUM: { label: "Prioridad media", tone: "border-brand-accent/40 bg-brand-accent/[0.10] text-brand-accent" },
  LOW: { label: "Prioridad baja", tone: "border-brand-silver/30 bg-brand-silver/[0.10] text-brand-silver" },
};
const cleanBio = (t) => String(t || "").replace(/\s*\n+\s*/g, " · ").replace(/\s{2,}/g, " ").trim();

const SOURCE_ICON = { instagram_oficial: Instagram, publico_apify: Globe, external_or_user_provided: MousePointerClick };

// Textos mostrados al usuario (nunca revela cola, proveedor ni otros usuarios).
const JOB_UI = {
  preparando: { label: "Preparando búsqueda…", spinner: true },
  buscando: { label: "Buscando oportunidades…", spinner: true },
  analizando: { label: "Analizando candidatos…", spinner: true },
  seleccionando: { label: "Seleccionando las mejores oportunidades…", spinner: true },
  preparadas: { label: "Oportunidades preparadas", spinner: false },
  sin_encaje: { label: "No se encontraron oportunidades suficientemente relevantes. Esta búsqueda no se descuenta de tu plan.", spinner: false },
  no_completada: { label: "No hemos podido completar la búsqueda. No se ha descontado tu consumo.", spinner: false },
  cancelada: { label: "Búsqueda cancelada.", spinner: false },
};
// v5: ejemplo del campo «Cuentas parecidas» según el objetivo elegido.
const REF_EXAMPLE = { cliente: "@una_cuenta_como_tu_cliente_ideal", socio: "@alguien_que_ya_hace_lo_que_buscas", afiliado: "@colaborador_que_ya_tienes", colaborador: "@colaborador_que_ya_tienes", recomendador: "@referente_de_tu_sector", seguidor: "@creador_de_tu_tematica", equipo: "@alguien_que_ya_hace_lo_que_buscas" };
const ACTIVE = new Set(["preparando", "buscando", "analizando", "seleccionando"]);

// v6: ubicación por defecto = mercado elegido en «Mi negocio» (el cliente puede cambiarla para esta búsqueda).
const marketLabel = (brain) => {
  const scope = brain?.market_scope;
  if (scope === "online") return "";
  const codes = brain?.market_countries || [];
  if (scope === "spain" && !codes.length) return "España";
  if (codes.length) {
    let names;
    try {
      const dn = new Intl.DisplayNames(["es"], { type: "region" });
      names = codes.map((c) => dn.of(String(c).toUpperCase())).join(", ");
    } catch (e) { names = codes.join(", "); }
    // v15: ciudad o zona del negocio («Valencia, España»).
    const city = (brain?.market_city || "").trim();
    return city && codes.length === 1 ? `${city}, ${names}` : names;
  }
  return brain?.market || "";
};

const OTHER_BRAND_OBJECTIVES = new Set(["socio", "equipo", "afiliado"]);

// Consultas que suelen dar pocos resultados en Instagram (frases largas).
const longQueries = (terms) => (terms || []).filter((t) => String(t).trim().split(/\s+/).length > 3);

export default function Opportunities() {
  const [brain, setBrain] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [avatarId, setAvatarId] = useState("");
  const [objective, setObjective] = useState("");
  const pendingSugRef = useRef(null);
  const [keywords, setKeywords] = useState("");
  const [location, setLocation] = useState("");
  const [refAccounts, setRefAccounts] = useState("");
  const [exclusions, setExclusions] = useState("");
  // v6: al buscar socios/distribuidores/equipo, incluir (con aviso) a quien ya trabaja con otra marca.
  const [includeOtherBrands, setIncludeOtherBrands] = useState(true);
  const [plan, setPlan] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const idemRef = useRef("");
  const [job, setJob] = useState(null); // { job_id, status(public), message, active, cancelable, result_count }
  const [candidates, setCandidates] = useState([]);
  // v6: oportunidades de búsquedas anteriores aún sin guardar ni descartar (no desaparecen al buscar de nuevo).
  const [pending, setPending] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [saving, setSaving] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const pollRef = useRef(null);
  // v27g: la búsqueda ya entregada sigue buscando más por detrás; aquí se van sumando sin quitar nada
  const lateRef = useRef(null);
  const handledRef = useRef(new Set());
  const resultsRef = useRef(null);
  const previewRef = useRef(null);
  const pendingRef = useRef(null);

  useEffect(() => {
    api.get("/business-brain").then((r) => {
      setBrain(r.data || {});
      if (r.data && r.data.include_other_brands === false) setIncludeOtherBrands(false);
    }).catch(() => setBrain({}));
    // Recuperar búsqueda existente (activa o última) al entrar. NO lanza búsquedas.
    api.get("/opportunities/search/current").then((r) => {
      if (r.data.job) applyJob(r.data.job, true);
      loadPending(r.data.job?.job_id || "");
    }).catch(() => loadPending(""));
    return () => { clearInterval(pollRef.current); clearInterval(lateRef.current); };
  }, []); // eslint-disable-line

  const avatars = useMemo(() => (brain?.avatars_list || []).filter((a) => a && a.name), [brain]);
  const currentAvatar = avatars.find((a) => (a.id || a.name) === avatarId);

  useEffect(() => {
    if (avatars.length && !avatarId) setAvatarId(avatars[0].id || avatars[0].name);
  }, [avatars, avatarId]);

  useEffect(() => {
    if (!currentAvatar || !brain) return;
    setKeywords(deriveProfileKeys(currentAvatar, brain).join(", "));
    // v15: los perfiles antiguos solo traen el país («España»); si el negocio tiene zona, manda la zona.
    const ml = marketLabel(brain);
    const al = (currentAvatar.location || "").trim();
    setLocation(al && !((brain?.market_city || "").trim() && ml.endsWith(al)) ? al : ml);
    const avObj = (currentAvatar.objective && OBJECTIVES.some((o) => o.k === currentAvatar.objective)) ? currentAvatar.objective : currentAvatar.type;
    if (avObj && OBJECTIVES.some((o) => o.k === avObj)) setObjective(avObj);
    if (pendingSugRef.current) { fillSuggestion(pendingSugRef.current); pendingSugRef.current = null; }
  }, [avatarId, brain]); // eslint-disable-line

  const loadPending = (excludeJob) => {
    api.get("/opportunities/pending", { params: { exclude_job: excludeJob || "" } })
      .then((r) => setPending(r.data.candidates || [])).catch(() => {});
  };

  const stopPoll = () => { clearInterval(pollRef.current); pollRef.current = null; };

  const ukey = (c) => (c.username || c.name || "").toLowerCase();
  const addLate = async (jobId) => {
    try {
      const r = await api.get(`/opportunities/search/${jobId}`);
      const fresh = r.data.job.candidates || [];
      let added = 0;
      setCandidates((cs) => {
        const have = new Set(cs.map(ukey));
        const extra = fresh.filter((c) => !have.has(ukey(c)) && !handledRef.current.has(ukey(c)));
        added = extra.length;
        return [...cs, ...extra];
      });
      setTimeout(() => { if (added > 0) toast.success(added === 1 ? "Hemos encontrado 1 oportunidad más." : `Hemos encontrado ${added} oportunidades más.`); }, 0);
    } catch (e) { /* noop */ }
  };
  const startLate = (jobId) => {
    clearInterval(lateRef.current);
    lateRef.current = setInterval(async () => {
      try {
        const r = await api.get("/opportunities/search/current");
        const j = r.data.job;
        if (!j || j.job_id !== jobId) { clearInterval(lateRef.current); return; }
        if (!j.completando) {
          clearInterval(lateRef.current);
          setJob(j);
          await addLate(jobId);
        }
      } catch (e) { /* sigue mirando */ }
    }, 8000);
  };

  const loadResults = async (jobId) => {
    try {
      const r = await api.get(`/opportunities/search/${jobId}`);
      setCandidates(r.data.job.candidates || []);
      await api.post(`/opportunities/search/${jobId}/reviewed`).catch(() => {});
    } catch (e) { /* noop */ }
  };

  const [loadingMore, setLoadingMore] = useState(false);
  const loadMore = async () => {
    if (!job?.job_id) return;
    setLoadingMore(true);
    try {
      const r = await api.post(`/opportunities/search/${job.job_id}/more`);
      if (r.data.added?.length) setCandidates((cs) => [...cs, ...r.data.added]);
      setJob((j) => ({ ...j, reserved_count: r.data.remaining }));
      if (!r.data.added?.length) toast.info(r.data.fresh ? "No quedan más oportunidades guardadas." : "Las oportunidades guardadas ya no están vigentes; lanza una nueva búsqueda.");
    } catch (e) { toast.error(apiError(e)); }
    setLoadingMore(false);
  };

  const applyJob = (j, initial = false) => {
    setJob(j);
    // El selector refleja el perfil REAL asociado al job recuperado (no el primero de la lista).
    if (j.avatar_id) setAvatarId(j.avatar_id);
    if (ACTIVE.has(j.status)) {
      startPoll(j.job_id);
    } else if (j.status === "preparadas") {
      if (!initial || j.result_count > 0) loadResults(j.job_id);
      if (j.completando) startLate(j.job_id);
    }
  };

  const startPoll = (jobId) => {
    stopPoll();
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get("/opportunities/search/current");
        const j = r.data.job;
        if (!j) { stopPoll(); return; }
        setJob(j);
        if (!ACTIVE.has(j.status)) {
          stopPoll();
          loadPending(j.job_id);
          if (j.status === "preparadas") {
            await loadResults(j.job_id);
            toast.success("Tus oportunidades están preparadas.");
            if (j.completando) startLate(j.job_id);
          } else if (j.status === "sin_encaje") {
            toast.info("No hemos encontrado oportunidades con suficiente encaje. Esta búsqueda no se descuenta de tu plan.");
          } else if (j.status === "no_completada") {
            toast.error("No hemos podido completar la búsqueda. No se ha descontado tu consumo.");
          } else if (j.status === "necesita_info") {
            toast.info("Necesito un par de datos de tu negocio antes de buscar.");
          }
        }
      } catch (e) { /* keep polling */ }
    }, 3000);
  };

  const regeneratePlan = async () => {
    if (!currentAvatar) return;
    setSubmitting(true);
    try {
      const r = await api.post("/opportunities/plan", {
        avatar_id: currentAvatar.id || "", avatar_name: currentAvatar.name, objective,
        keywords: splitList(keywords), location: location.trim(),
        reference_accounts: splitList(refAccounts), exclusions: splitList(exclusions), force: true,
      });
      setPlan(r.data.plan);
      toast.success("Plan regenerado.");
      scrollToPreview();
    } catch (e) { toast.error(apiError(e)); } finally { setSubmitting(false); }
  };

  const openPreview = async () => {
    if (!currentAvatar) { toast.error("Selecciona un avatar primero."); return; }
    setSubmitting(true);
    try {
      const r = await api.post("/opportunities/plan", {
        avatar_id: currentAvatar.id || "", avatar_name: currentAvatar.name, objective,
        keywords: splitList(keywords), location: location.trim(),
        reference_accounts: splitList(refAccounts), exclusions: splitList(exclusions),
      });
      idemRef.current = (window.crypto?.randomUUID && window.crypto.randomUUID()) || String(Date.now());
      setPlan(r.data.plan); setShowPreview(true);
      scrollToPreview();
    } catch (e) { toast.error(apiError(e)); } finally { setSubmitting(false); }
  };

  const startSearch = async () => {
    if (submitting || (job && job.active) || !plan) return;
    setSubmitting(true);
    try {
      const r = await api.post("/opportunities/search", {
        avatar_id: currentAvatar.id || "", avatar_name: currentAvatar.name, objective,
        keywords: splitList(keywords), location: location.trim(),
        reference_accounts: splitList(refAccounts), exclusions: splitList(exclusions),
        idempotency_key: idemRef.current,
        include_other_brands: includeOtherBrands,
        plan: { plan_key: plan.plan_key, searchTerms: plan.searchTerms, location: plan.location, exclusionSignals: plan.exclusionSignals },
      });
      setShowPreview(false);
      // v6: las oportunidades sin gestionar de la búsqueda anterior pasan a «Pendientes de revisar».
      if (candidates.length) {
        const prevId = job?.job_id || "";
        setPending((pd) => {
          const keys = new Set(pd.map((c) => (c.username || c.name || "").toLowerCase()));
          const moved = candidates.filter((c) => !keys.has((c.username || c.name || "").toLowerCase()))
            .map((c) => ({ ...c, search_job_id: c.search_job_id || prevId }));
          return [...moved, ...pd];
        });
      }
      setCandidates([]);
      applyJob(r.data.job);
      window.dispatchEvent(new Event("ca:search-started"));
      toast.success("Estamos buscando oportunidades para ti. Te avisaremos cuando estén preparadas.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const cancelSearch = async () => {
    if (!job) return;
    try {
      const r = await api.post(`/opportunities/search/${job.job_id}/cancel`);
      stopPoll();
      setJob(r.data.job);
      toast("Búsqueda cancelada.");
    } catch (e) { toast.error(apiError(e)); }
  };

  const listSetter = (src) => (src === "pending" ? setPending : setCandidates);
  const saveCandidate = async (c, idx, src = "new") => {
    setSaving(`${src}-${idx}`);
    try {
      const r = await api.post("/opportunities/save", { candidate: c, search_job_id: c.search_job_id || job?.job_id || "" });
      if (r.data.duplicate) toast.info(`Ya tenías a ${c.name} en tus contactos.`);
      else toast.success(`${c.name} guardado en contactos con su primera acción.`);
      handledRef.current.add(ukey(c));
      listSetter(src)((list) => list.filter((x, i) => (src === "pending" ? x !== c : i !== idx)));
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(""); }
  };
  const discardCandidate = (c, idx, src = "new") => {
    handledRef.current.add(ukey(c));
    listSetter(src)((list) => list.filter((x, i) => (src === "pending" ? x !== c : i !== idx)));
    // P1-B: registrar el descarte (fire-and-forget; no bloquea la UI).
    api.post("/opportunities/discard", { candidate: c, search_job_id: c.search_job_id || job?.job_id || "" }).catch(() => {});
  };
  // v26b: «No proponer a esta persona» → «La conozco» (veto: ni lead ni lugar) o «No me interesa» (descarte).
  const [vetoMenu, setVetoMenu] = useState("");
  const vetoCandidate = async (c, idx, src = "new") => {
    setVetoMenu("");
    listSetter(src)((list) => list.filter((x, i) => (src === "pending" ? x !== c : i !== idx)));
    try {
      const r = await api.post("/vetoed-accounts", { account: c.username, reason: "la_conozco", search_job_id: c.search_job_id || job?.job_id || "" });
      if (r.data.suggest_review) {
        toast("¿Conoces a más gente que pueda salir aquí?", {
          description: "Añade sus cuentas en «Mi negocio» → «Cuentas que no quieres usar» y no te las propondremos.",
          action: { label: "Ir a Mi negocio", onClick: () => { window.location.href = "/app/brain"; } },
          duration: 9000,
        });
      } else {
        toast.success(`No volveremos a proponerte a @${c.username}.`);
      }
    } catch (e) { toast.error(apiError(e)); }
  };
  // v12: «Probar esta búsqueda»: rellena el formulario con la alternativa propuesta; el cliente la revisa y la lanza.
  const formRef = useRef(null);
  const fillSuggestion = (sug) => {
    setKeywords((sug.keywords || []).join(", "));
    setLocation(sug.location || "");
    setRefAccounts((sug.reference_accounts || []).join(", "));
    setExclusions((sug.exclusions || []).join(", "));
  };

  const trySuggestion = (sug) => {
    if (!sug) return;
    // v19: si la propuesta es otro perfil del cliente, se selecciona ese perfil y luego se rellenan sus palabras
    // (al cambiar de perfil el formulario se reinicia con las del perfil).
    const target = sug.profile && avatars.find((a) => a.name === sug.profile);
    if (target && (target.id || target.name) !== avatarId) {
      pendingSugRef.current = sug;
      setAvatarId(target.id || target.name);
    } else {
      fillSuggestion(sug);
    }
    setShowPreview(false); setPlan(null);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
    toast.success("Hemos puesto la búsqueda propuesta. Revísala y pulsa para ver el plan.");
  };

  // v26b · Pocos leads con interés claro en las últimas búsquedas: el usuario elige el cambio (la app no lo hace sola).
  // v27b · «Contarnos más»: una respuesta corta que se añade a «Mi negocio».
  const [answerFor, setAnswerFor] = useState(null);
  const [answerText, setAnswerText] = useState("");
  const chooseCascade = async (opt) => {
    if (opt.kind === "responder" && answerFor !== opt) { setAnswerFor(opt); return; }
    let res;
    try {
      res = await api.post("/opportunities/cascade/choose", { job_id: job.job_id, kind: opt.kind,
        answer: opt.kind === "responder" ? answerText : "" });
    } catch (e) { toast.error(apiError(e)); return; }
    setJob((j) => (j ? { ...j, cascade_advice: null } : j));
    setAnswerFor(null); setAnswerText("");
    if (opt.kind === "usar_perfil_sugerido" || opt.kind === "responder") {
      try { const fresh = await api.get("/business-brain"); setBrain(fresh.data || {}); } catch (e) { /* sigue igual */ }
      const av = res?.data?.avatar;
      if (av) setAvatarId(av.id || av.name);
    }
    if (opt.kind === "perfil_hermano") {
      const target = avatars.find((a) => a.name === opt.profile);
      if (target) setAvatarId(target.id || target.name);
    } else if (opt.kind === "ampliar_zona") {
      setLocation(opt.location || "");
    }
    setShowPreview(false); setPlan(null);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
    toast.success(opt.kind === "seguir_igual" ? "Seguimos igual."
      : opt.kind === "responder" ? "Gracias. Lo hemos añadido a «Mi negocio» para las próximas búsquedas."
      : opt.kind === "usar_perfil_sugerido" ? "Perfil nuevo guardado y elegido. Pulsa «Buscar» cuando quieras."
      : "Hecho. Pulsa «Buscar» cuando quieras y lo aplicamos.");
  };

  // v19 · Negocio local sin resultados buscando clientes: pasar a recomendadores de su zona.
  const [recBusy, setRecBusy] = useState(false);
  const tryRecommenders = async () => {
    const pick = (list) => list.find((a) => (a.objective || a.type) === "recomendador");
    let rec = pick(avatars);
    if (!rec) {
      setRecBusy(true);
      try {
        const r = await api.post("/business-brain/avatars/suggest", { objective: "recomendador" });
        if (r.data?.status === "needs_information") {
          toast.info(r.data.needs_information?.questions?.[0] || "Necesito un par de datos de tu negocio antes de proponer perfiles.");
          return;
        }
        const fresh = await api.get("/business-brain");
        setBrain(fresh.data || {});
        rec = pick((fresh.data?.avatars_list || []).filter((a) => a && a.name));
      } catch (e) { toast.error(apiError(e)); return; } finally { setRecBusy(false); }
    }
    if (!rec) { toast.info("No hemos podido proponer recomendadores. Puedes crearlos en «Mi negocio»."); return; }
    setAvatarId(rec.id || rec.name);
    setShowPreview(false); setPlan(null);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
    toast.success(`Hemos elegido el perfil «${rec.name}». Revísalo y pulsa para ver el plan.`);
  };

  const scrollToPreview = () => setTimeout(() => previewRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);

  const suggestAvatars = async () => {
    setGenerating(true);
    try {
      const r = await api.post("/business-brain/avatars/suggest", { objective: objective || "" });
      if (r.data?.status === "needs_information") {
        const qs = r.data.needs_information?.questions || [];
        toast.info(qs[0] || "Necesito un par de datos de tu negocio antes de proponer avatares.");
      } else {
        const fresh = await api.get("/business-brain");
        setBrain(fresh.data || {});
        toast.success("CaptAcción ha propuesto tus avatares. Puedes verlos y editarlos en «Mi negocio».");
      }
    } catch (e) { toast.error(apiError(e)); } finally { setGenerating(false); }
  };

  const renderCard = (c, i, src) => {
              const SrcIcon = SOURCE_ICON[c.discovery_method] || Globe;
              const fa = c.first_action || {};
              const key = `${src}-${i}`;
              const isOpen = !!expanded[key];
              return (
                <div key={c.username || i} data-testid={src === "new" ? `opp-card-${i}` : `opp-pending-card-${i}`} className="ca-card ca-card-hover p-5 animate-fade-up" style={{ animationDelay: `${i * 0.05}s` }}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 items-start gap-3">
                      {c.photo_url ? (
                        <img src={c.photo_url} alt="" className="h-10 w-10 shrink-0 rounded-full object-cover" />
                      ) : (
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-accent/10 text-brand-accent"><Users size={18} /></span>
                      )}
                      <div className="min-w-0">
                        <p className="truncate text-sm font-bold text-brand-ink">{c.name}</p>
                        {src === "pending" && c.from_search?.date && <p className="text-[11px] text-brand-muted/80">De tu búsqueda del {c.from_search.date.split("-").reverse().join("/")}{c.from_search.avatar_name ? ` · ${c.from_search.avatar_name}` : ""}</p>}
                        {c.username && <p className="truncate text-xs text-brand-muted">@{c.username}</p>}
                        <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-brand-muted">
                          <span className="inline-flex items-center gap-1 rounded border border-brand-line bg-brand-panel2 px-1.5 py-0.5"><SrcIcon size={11} /> {c.discovery_source}</span>
                          <span className="inline-flex items-center gap-1 rounded border border-brand-line bg-brand-panel2 px-1.5 py-0.5">Contactar por {CHANNEL_META[c.recommended_channel]?.label || "Instagram"}</span>
                          {c.found_by_alt_profile && <span className="inline-flex items-center gap-1 rounded border border-brand-line bg-brand-panel2 px-1.5 py-0.5" data-testid={`opp-alt-${i}`}>Encontrado con otro perfil: {c.found_by_alt_profile}</span>}
                          {c.location_status && <span className="inline-flex items-center gap-1 rounded border border-brand-line bg-brand-panel2 px-1.5 py-0.5" data-testid={`opp-location-${i}`}>Ubicación: {c.zone_status === "match" ? "en tu zona" : c.zone_status === "other_city" ? "fuera de tu zona" : (GEO_LABEL[c.geo_status] || GEO_LABEL[c.location_status] || "sin confirmar")}</span>}
                          <span>Para: {typeLabel(c.objective) || c.objective}</span>
                          {(c.other_brand || c.qualification?.other_brand) && <span className="inline-flex items-center gap-1 rounded border border-brand-accent/40 bg-brand-accent/[0.08] px-1.5 py-0.5 font-semibold text-brand-accent" data-testid={`opp-other-brand-${i}`}>Trabaja con otra marca: {c.other_brand || c.qualification?.other_brand}</span>}
                        </div>
                      </div>
                    </div>
                    {PRIO_META[c.priority] ? (
                      <span data-testid={`opp-priority-${i}`} className={`shrink-0 rounded-md border px-2 py-0.5 text-[10px] font-bold uppercase ${PRIO_META[c.priority].tone}`}>{PRIO_META[c.priority].label}</span>
                    ) : (
                      <span className={`shrink-0 rounded-md border px-2 py-0.5 text-[10px] font-bold uppercase ${CLASS_TONE[c.classification] || CLASS_TONE.cultivar}`}>{c.classification_label}</span>
                    )}
                  </div>

                  {LEAD_LABEL[c.lead_label] && (
                    <p className="mt-2 flex flex-wrap items-center gap-2 text-xs" data-testid={`opp-lead-label-${i}`}>
                      <span className={`rounded-md border px-2 py-0.5 font-bold ${LEAD_LABEL[c.lead_label].tone}`}>{LEAD_LABEL[c.lead_label].emoji} {LEAD_LABEL[c.lead_label].label}</span>
                      {c.label_reason && <span className="text-brand-muted">{c.label_reason}</span>}
                    </p>
                  )}
                  {c.reasons?.[0] && <p className="mt-2 text-xs text-brand-ink" data-testid={`opp-why-short-${i}`}>{c.reasons[0]}</p>}
                  {c.bio_public && <p className="mt-1 line-clamp-2 text-xs text-brand-muted">{cleanBio(c.bio_public)}</p>}

                  <button type="button" onClick={() => setExpanded((e) => ({ ...e, [key]: !e[key] }))} data-testid={`opp-view-${i}`} className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-brand-accent">
                    {isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />} Ver oportunidad
                  </button>

                  {isOpen && (
                    <div className="mt-2 space-y-3 rounded-md border border-brand-line bg-brand-panel2/30 p-3 text-xs">
                      {c.reasons?.length > 0 && (
                        <div data-testid={`opp-why-${i}`}><p className="font-bold text-brand-ink">Por qué te mostramos este perfil</p><ul className="mt-1 list-disc pl-4 text-brand-muted">{c.reasons.map((r, k) => <li key={k}>{r}</li>)}</ul></div>
                      )}
                      {c.observed?.length > 0 && (
                        <div data-testid={`opp-facts-${i}`}><p className="font-bold text-brand-ink">Señales verificadas</p><ul className="mt-1 list-disc pl-4 text-brand-muted">{c.observed.map((r, k) => <li key={k}>{r}</li>)}</ul></div>
                      )}
                      {c.inferred?.length > 0 && (
                        <div><p className="font-bold text-brand-ink">Interpretación (no verificada)</p><p className="mt-0.5 text-brand-muted">{c.inferred.join(" · ")}</p></div>
                      )}
                      {c.to_confirm?.length > 0 && (
                        <div data-testid={`opp-unknowns-${i}`}><p className="font-bold text-brand-ink">Por confirmar</p><p className="mt-0.5 text-brand-muted">{c.to_confirm.join(" · ")}</p></div>
                      )}
                      {c.counter_signals?.length > 0 && (
                        <div><p className="font-bold text-brand-ink">Señales en contra</p><p className="mt-0.5 text-brand-muted">{c.counter_signals.join(" · ")}</p></div>
                      )}
                      {c.next_step && (
                        <div><p className="font-bold text-brand-ink">Siguiente paso</p><p className="mt-0.5 text-brand-muted">{c.next_step}</p></div>
                      )}
                      {c.source_url && <a href={c.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-brand-accent"><ExternalLink size={12} /> Ver origen</a>}
                      {(fa.what || fa.message) && (
                        <div className="rounded border border-brand-accent/30 bg-brand-accent/[0.05] p-2">
                          <p className="flex items-center gap-1 font-bold text-brand-accent"><Sparkles size={12} /> Primera acción sugerida</p>
                          {fa.what && <p className="mt-1 text-brand-ink"><span className="font-semibold">Qué hacer:</span> {fa.what}</p>}
                          {Array.isArray(fa.how) && fa.how.length > 0 && <ol className="mt-1 list-decimal pl-4 text-brand-muted">{fa.how.map((h, k) => <li key={k}>{h}</li>)}</ol>}
                          {fa.type === "comentario" && fa.comment && <p className="mt-1 rounded bg-brand-canvas p-2 text-brand-ink"><span className="font-semibold">1. Comentario:</span> {fa.comment}</p>}
                          {fa.message && <p className="mt-1 rounded bg-brand-canvas p-2 text-brand-ink"><span className="font-semibold">{fa.type === "comentario" && fa.comment ? "2. Mensaje directo después:" : "Mensaje (revísalo y envíalo tú):"}</span> {fa.message}</p>}
                          {fa.type === "audio" && fa.audio_script && <p className="mt-1 rounded bg-brand-canvas p-2 text-brand-ink"><span className="font-semibold">Guion del audio:</span> {fa.audio_script}</p>}
                          {fa.why_this_action && <p className="mt-1 text-brand-muted"><span className="font-semibold">Por qué así:</span> {fa.why_this_action}</p>}
                          {fa.avoid && <p className="mt-1 text-brand-muted"><span className="font-semibold">Evita:</span> {fa.avoid}</p>}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="mt-3 flex flex-wrap gap-2">
                    <button type="button" onClick={() => saveCandidate(c, i, src)} disabled={saving === key} data-testid={`opp-save-${i}`} className="inline-flex items-center gap-1.5 rounded-md bg-brand-accent px-3 py-1.5 text-xs font-bold text-brand-canvas hover:brightness-110 disabled:opacity-60">
                      {saving === key ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Guardar en contactos
                    </button>
                    <div className="relative">
                      <button type="button" onClick={() => setVetoMenu(vetoMenu === key ? "" : key)} data-testid={`opp-discard-${i}`} aria-expanded={vetoMenu === key} className="inline-flex items-center gap-1.5 rounded-md border border-brand-line px-3 py-1.5 text-xs font-bold text-brand-muted hover:border-brand-neg hover:text-brand-neg">
                        <X size={13} /> No proponer a esta persona
                      </button>
                      {vetoMenu === key && (
                        <div className="absolute left-0 z-10 mt-1 w-64 rounded-md border border-brand-line bg-brand-panel p-1 text-xs shadow-lg" data-testid={`opp-veto-menu-${i}`}>
                          <button type="button" onClick={() => vetoCandidate(c, i, src)} data-testid={`opp-veto-know-${i}`} className="block w-full rounded px-3 py-2 text-left hover:bg-brand-panel2">
                            <b className="text-brand-ink">La conozco</b><span className="block text-brand-muted">No la propondremos ni buscaremos entre sus seguidores.</span>
                          </button>
                          <button type="button" onClick={() => { setVetoMenu(""); discardCandidate(c, i, src); }} data-testid={`opp-veto-notint-${i}`} className="block w-full rounded px-3 py-2 text-left hover:bg-brand-panel2">
                            <b className="text-brand-ink">No me interesa</b><span className="block text-brand-muted">La quitamos de tus oportunidades.</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
  };

  // Si una pendiente reaparece en la búsqueda actual, se muestra solo una vez (en la actual).
  const curKeys = new Set(candidates.map((x) => (x.username || x.name || "").toLowerCase()));
  const pendingView = pending.filter((x) => !curKeys.has((x.username || x.name || "").toLowerCase()));
  const noAvatars = brain && avatars.length === 0;
  const isActive = job && ACTIVE.has(job.status);
  const uiState = job ? JOB_UI[job.status] : null;

  return (
    <div className="space-y-8">
      <PageHeader title="Encontrar oportunidades" subtitle="Descubre candidatos reales a partir de tus avatares y objetivos. CaptAcción usa internamente las fuentes disponibles." testId="opportunities-header" />
      <ProfileNudge testId="opportunities-profile-nudge" />
      {!noAvatars && <DeliveryMode avatars={avatars} />}

      {noAvatars ? (
        <div className="rounded-lg border border-dashed border-brand-line bg-brand-panel p-8 text-center" data-testid="opp-no-avatars">
          <Sparkles size={24} className="mx-auto mb-3 text-brand-accent" />
          <p className="text-sm font-semibold text-brand-ink">¿Aún no sabes a quién buscar?</p>
          <p className="mx-auto mt-1 max-w-md text-xs text-brand-muted">No hace falta que lo decidas tú. CaptAcción puede proponer tus avatares a partir de tu negocio y tus objetivos. Luego podrás verlos y editarlos en «Mi negocio».</p>
          <button onClick={suggestAvatars} disabled={generating} data-testid="opp-suggest-avatars" className="mt-4 inline-flex items-center gap-2 rounded-md bg-brand-accent px-4 py-2 text-xs font-bold text-brand-canvas hover:brightness-110 disabled:opacity-60">
            {generating ? <><Loader2 size={14} className="animate-spin" /> Proponiendo avatares…</> : <>Proponer mis avatares <Sparkles size={14} /></>}
          </button>
          <div className="mt-3"><Link to="/app/brain" className="text-xs font-semibold text-brand-muted underline hover:text-brand-accent">o definirlos yo en «Mi negocio»</Link></div>
        </div>
      ) : (
        <section ref={formRef} className="ca-card p-6 animate-fade-up" data-testid="opp-form">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="ca-eyebrow mb-1.5 block">Avatar</label>
              <select value={avatarId} onChange={(e) => setAvatarId(e.target.value)} disabled={isActive} data-testid="opp-avatar-select" className="ca-select disabled:opacity-60">
                {avatars.map((a) => <option key={a.id || a.name} value={a.id || a.name}>{a.name}{a.type ? ` · ${TYPE_META[a.type]?.label || a.type}` : ""}</option>)}
              </select>
            </div>
            <div>
              <label className="ca-eyebrow mb-1.5 block">Objetivo</label>
              <select value={objective} onChange={(e) => setObjective(e.target.value)} disabled={isActive} data-testid="opp-objective-select" className="ca-select disabled:opacity-60">
                <option value="" disabled>Selecciona qué buscas…</option>
                {OBJECTIVES.map((o) => <option key={o.k} value={o.k}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="ca-eyebrow mb-1.5 block">Claves del perfil</label>
              <p className="mb-1.5 text-xs text-brand-muted" data-testid="opp-keywords-help">Lo que CaptAcción ha entendido del perfil. Se usan como contexto; no son las búsquedas literales.</p>
              <input value={keywords} onChange={(e) => setKeywords(e.target.value)} disabled={isActive} data-testid="opp-keywords-input" className="ca-input disabled:opacity-60" placeholder="coach, marca personal, servicios" />
            </div>
            <div>
              <label className="ca-eyebrow mb-1.5 block">Ubicación <span className="font-normal normal-case text-brand-muted/60">(opcional)</span></label>
              <input value={location} onChange={(e) => setLocation(e.target.value)} disabled={isActive} data-testid="opp-location-input" className="ca-input disabled:opacity-60" placeholder="España, Valencia, online…" />
              <p className="mt-1 text-[11px] text-brand-muted" data-testid="opp-location-help">Ciudad o país solo para esta búsqueda. Con una ciudad, priorizamos cuentas de esa ciudad. Vacío = el mercado de «Mi negocio».</p>
            </div>
            <div className="md:col-span-2">
              <label className="mb-1.5 block text-sm font-bold text-brand-accent" data-testid="opp-refs-label">
                Cuentas parecidas a los perfiles que buscas, o un referente de tu sector al que sigan
                <span className="ml-1 text-xs font-normal text-brand-muted/70">(opcional, hasta 2)</span>
              </label>
              <input value={refAccounts} onChange={(e) => setRefAccounts(e.target.value)} disabled={isActive} data-testid="opp-refs-input" className="ca-input disabled:opacity-60" placeholder={REF_EXAMPLE[objective] || REF_EXAMPLE.cliente} />
              <details className="mt-1.5 text-xs text-brand-muted" data-testid="opp-refs-more">
                <summary className="cursor-pointer select-none font-semibold text-brand-muted hover:text-brand-accent">Ver ejemplos</summary>
                <p className="mt-1.5">CaptAcción buscará cuentas parecidas y entre sus seguidores: así encuentras perfiles que las palabras clave no sacan.</p>
                <ul className="mt-1.5 list-disc space-y-1 pl-4">
                  <li><b className="text-brand-ink">Clientes:</b> una cuenta que ya sea tu cliente ideal (por ejemplo, una nutricionista si ayudas a nutricionistas) o un referente al que siguen.</li>
                  <li><b className="text-brand-ink">Socios o distribuidores:</b> alguien de tu equipo o de tu sector que ya haga lo que buscas; aparecerán perfiles parecidos.</li>
                  <li><b className="text-brand-ink">Colaboradores o alianzas:</b> un profesional complementario con el que ya colaboras.</li>
                  <li><b className="text-brand-ink">Seguidores:</b> un creador de tu temática; sus seguidores comparten tus intereses.</li>
                </ul>
                <p className="mt-1.5">Mejor cuentas públicas y activas. No hace falta tu competencia directa; si la pones, no se le escribirá a ella, solo se usa para encontrar perfiles parecidos.</p>
              </details>
            </div>
            <div className="md:col-span-2">
              <label className="ca-eyebrow mb-1.5 block">Exclusiones <span className="font-normal normal-case text-brand-muted/60">(opcional, qué evitar, separadas por comas)</span></label>
              <input value={exclusions} onChange={(e) => setExclusions(e.target.value)} disabled={isActive} data-testid="opp-exclusions-input" className="ca-input disabled:opacity-60" placeholder="competencia directa, otra ciudad" />
            </div>
            {OTHER_BRAND_OBJECTIVES.has(objective) && (
              <label className="md:col-span-2 flex items-start gap-2 text-xs text-brand-ink" data-testid="opp-other-brands">
                <input type="checkbox" checked={includeOtherBrands} onChange={(e) => setIncludeOtherBrands(e.target.checked)} disabled={isActive} className="mt-0.5 accent-[#d4a94f]" data-testid="opp-other-brands-checkbox" />
                <span><b className="font-semibold">Incluir personas que ya trabajan con una marca de otro sector</b> (p. ej. si vendes cosmética, alguien de un multinivel de nutrición). Te las marcaremos para que decidas. Quien trabaja con una marca de tu mismo sector es competencia y nunca se muestra.</span>
              </label>
            )}
          </div>
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <p className="text-[11px] text-brand-muted">Te mostraremos un resumen editable antes de empezar. Máximo 5 oportunidades por búsqueda.</p>
            {isActive ? (
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-2 rounded-md border border-brand-accent/40 bg-brand-accent/[0.08] px-4 py-2.5 text-sm font-bold text-brand-accent" data-testid="opp-inprogress-btn">
                  <Loader2 size={15} className="animate-spin" /> Búsqueda en curso
                </span>
              </div>
            ) : (
              <button type="button" onClick={openPreview} disabled={submitting} data-testid="opp-search-btn" className="inline-flex items-center gap-2 rounded-md bg-brand-accent px-5 py-2.5 text-sm font-bold text-brand-canvas transition-all hover:brightness-110 disabled:opacity-60">
                {submitting ? <Loader2 size={15} className="animate-spin" /> : <Radar size={15} />} Encontrar oportunidades
              </button>
            )}
          </div>
        </section>
      )}

      {/* Pantalla previa editable (sin nombres técnicos) */}
      {showPreview && plan && !isActive && (
        <section ref={previewRef} className="ca-card ca-card-gold p-6 animate-fade-up" data-testid="opp-preview">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-bold text-brand-ink">Revisa tu búsqueda</p>
            <span className="rounded-md border border-brand-line bg-brand-panel2 px-2 py-0.5 text-[11px] font-semibold text-brand-muted" data-testid="opp-planner-source">
              {plan.planner_source === "ai" ? "Plan preparado con IA" : "Plan preparado con reglas de respaldo"}
            </span>
          </div>
          {plan.candidateDescription && (
            <p className="mt-2 text-xs text-brand-muted" data-testid="opp-candidate-desc"><span className="font-semibold text-brand-ink">A quién buscaremos:</span> {plan.candidateDescription}</p>
          )}
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            <div className="rounded-md border border-brand-line bg-brand-panel p-3 text-xs">
              <p className="text-brand-muted">Qué buscas</p>
              <p className="font-semibold text-brand-ink">{OBJECTIVES.find((o) => o.k === objective)?.label} · {currentAvatar?.name}</p>
              <p className="mt-2 text-brand-muted">Ruta elegida</p>
              <p className="font-semibold text-brand-accent" data-testid="opp-preview-route">{plan.route_label}</p>
              {(plan.topicsOrHashtags || []).length > 0 && (
                <>
                  <p className="mt-2 text-brand-muted">Temas</p>
                  <p className="text-brand-ink">{plan.topicsOrHashtags.join(" · ")}</p>
                </>
              )}
              {(plan.positiveSignals || []).length > 0 && (
                <>
                  <p className="mt-2 text-brand-muted">Señales de buen encaje</p>
                  <p className="text-brand-ink">{plan.positiveSignals.join(" · ")}</p>
                </>
              )}
            </div>
            <div className="rounded-md border border-brand-line bg-brand-panel p-3 text-xs space-y-2">
              <div>
                <label className="text-brand-muted">Consultas que usaremos</label>
                <input value={(plan.searchTerms || []).join(", ")} onChange={(e) => setPlan((p) => ({ ...p, searchTerms: splitList(e.target.value) }))} data-testid="opp-preview-terms" className="mt-1 w-full rounded border border-brand-line bg-brand-canvas px-2 py-1.5 text-brand-ink outline-none focus:border-brand-accent" />
                <p className="mt-1 text-[11px] text-brand-muted">Las consultas cortas (1-3 palabras) encuentran más cuentas. No hace falta poner la ciudad ni «España»: lo añadimos nosotros según la ubicación.</p>
                {longQueries(plan.searchTerms).length > 0 && (
                  <p className="mt-1 text-[11px] font-semibold text-brand-accent" data-testid="opp-long-query-warning">«{longQueries(plan.searchTerms)[0]}» es larga y puede dar pocos resultados; si no encuentra cuentas, probaremos versiones más cortas.</p>
                )}
              </div>
              <div>
                <label className="text-brand-muted">Ubicación o ámbito</label>
                <input value={plan.location || ""} onChange={(e) => setPlan((p) => ({ ...p, location: e.target.value }))} data-testid="opp-preview-location" className="mt-1 w-full rounded border border-brand-line bg-brand-canvas px-2 py-1.5 text-brand-ink outline-none focus:border-brand-accent" placeholder="Opcional" />
              </div>
              <div>
                <label className="text-brand-muted">Exclusiones</label>
                <input value={(plan.exclusionSignals || []).join(", ")} onChange={(e) => setPlan((p) => ({ ...p, exclusionSignals: splitList(e.target.value) }))} data-testid="opp-preview-exclusions" className="mt-1 w-full rounded border border-brand-line bg-brand-canvas px-2 py-1.5 text-brand-ink outline-none focus:border-brand-accent" />
              </div>
            </div>
          </div>
          {plan.reasoningSummary && (
            <p className="mt-3 rounded-md border border-brand-line bg-brand-panel p-3 text-[11px] text-brand-muted" data-testid="opp-reasoning"><span className="font-semibold text-brand-ink">Por qué proponemos este plan:</span> {plan.reasoningSummary}</p>
          )}
          <p className="mt-3 text-[11px] text-brand-muted">Máximo de oportunidades finales: <span className="font-semibold text-brand-ink">{plan.finalLimit || 5}</span>.</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <button type="button" onClick={startSearch} disabled={submitting} data-testid="opp-start-btn" className="inline-flex items-center gap-2 rounded-md bg-brand-accent px-5 py-2.5 text-sm font-bold text-brand-canvas hover:brightness-110 disabled:opacity-60">
              {submitting ? <Loader2 size={15} className="animate-spin" /> : <Radar size={15} />} Empezar búsqueda
            </button>
            <button type="button" onClick={() => setShowPreview(false)} data-testid="opp-modify-btn" className="inline-flex items-center gap-1.5 rounded-md border border-brand-line px-4 py-2.5 text-sm font-bold text-brand-ink hover:border-brand-accent hover:text-brand-accent">
              Modificar búsqueda
            </button>
            <button type="button" onClick={regeneratePlan} disabled={submitting} data-testid="opp-regen-btn" className="inline-flex items-center gap-1.5 rounded-md border border-brand-line px-4 py-2.5 text-sm font-bold text-brand-muted hover:border-brand-accent hover:text-brand-accent disabled:opacity-60">
              Generar un nuevo plan
            </button>
          </div>
        </section>
      )}

      {/* Estado de la búsqueda */}
      <div ref={resultsRef}>
        {uiState && ACTIVE.has(job.status) && (
          <SearchProgress job={job} onCancel={cancelSearch} pendingCount={pendingView.length} statusLabel={uiState.label}
            onShowPending={() => pendingRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })} />
        )}

        {job && job.status === "necesita_info" && (
          <div className="rounded-lg border border-brand-accent/40 bg-brand-accent/[0.06] p-5" data-testid="opp-needs-info">
            <p className="text-sm font-bold text-brand-ink">Necesito un par de datos antes de buscar</p>
            <p className="mt-1 text-xs text-brand-muted">Para encontrar a las personas adecuadas, completa esto en «Mi negocio» y vuelve a buscar:</p>
            <ul className="mt-2 list-disc pl-5 text-sm text-brand-ink">
              {(job.needs_information?.questions || []).map((q, k) => <li key={k} data-testid={`opp-need-q-${k}`}>{q}</li>)}
            </ul>
          </div>
        )}

        {job && job.status === "sin_encaje" && !job.empty_advice && (
          <div className="rounded-lg border border-brand-line bg-brand-panel p-6 text-center" data-testid="opp-empty">
            <p className="text-sm font-semibold text-brand-ink">No se encontraron oportunidades suficientemente relevantes</p>
            <p className="mt-1 text-xs text-brand-muted">Prueba con otras palabras, otra ubicación o cuentas de referencia distintas. Esta búsqueda no se descuenta de tu plan.</p>
          </div>
        )}
        {job && job.status === "sin_encaje" && job.empty_advice && (
          <EmptyAdvice advice={job.empty_advice} onTry={trySuggestion} onRecommenders={tryRecommenders} recommendersBusy={recBusy} disabled={isActive} />
        )}
        {job && job.status === "no_completada" && (
          <div className="rounded-lg border border-brand-neg/40 bg-brand-neg/[0.08] p-5 text-center text-sm text-brand-neg" data-testid="opp-error">No hemos podido completar la búsqueda. No se ha descontado tu consumo.</div>
        )}
        {job && job.status === "cancelada" && (
          <div className="rounded-lg border border-brand-line bg-brand-panel p-5 text-center text-sm text-brand-muted" data-testid="opp-cancelled">Búsqueda cancelada.</div>
        )}

        {job && job.status === "preparadas" && candidates.length > 0 && (
          <section className="space-y-3" data-testid="opp-results">
            {job.cascade_advice && (
              <div className="rounded-lg border border-brand-accent/40 bg-brand-accent/[0.06] p-4" data-testid="opp-cascade">
                <p className="text-sm font-bold text-brand-ink">{job.cascade_advice.reason}</p>
                {job.cascade_advice.detail && <p className="mt-1 text-xs text-brand-ink" data-testid="opp-cascade-detail">{job.cascade_advice.detail}</p>}
                <p className="mt-1 text-xs text-brand-muted">{job.cascade_advice.cause === "proveedor" ? "No es por tu perfil: no hace falta que cambies nada." : job.cascade_advice.cause === "perfil_ajustado" ? "Si te encaja, guárdalo para las próximas búsquedas; si no, revisa tu perfil en «Mi negocio»." : "¿Quieres cambiar algo para la próxima búsqueda? Tú eliges; no cambiamos nada sin que lo digas."}</p>
                {answerFor && (
                  <div className="mt-3 space-y-2" data-testid="opp-cascade-answer">
                    <p className="text-xs font-semibold text-brand-ink">{answerFor.question}</p>
                    <div className="flex gap-2">
                      <input value={answerText} onChange={(e) => setAnswerText(e.target.value)} maxLength={500}
                        className="flex-1 rounded-md border border-brand-line bg-white px-3 py-1.5 text-xs" placeholder="Tu respuesta" />
                      <button type="button" onClick={() => chooseCascade(answerFor)} disabled={answerText.trim().length < 3}
                        className="rounded-md bg-brand-accent px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50">Guardar</button>
                    </div>
                  </div>
                )}
                <div className="mt-3 flex flex-wrap gap-2">
                  {(job.cascade_advice.options || []).map((o) => (
                    <button key={o.kind} type="button" onClick={() => chooseCascade(o)} title={o.desc} data-testid={`opp-cascade-${o.kind}`}
                      className="rounded-md border border-brand-accent px-3 py-1.5 text-xs font-bold text-brand-accent hover:bg-brand-accent/10">{o.label}</button>
                  ))}
                  <button type="button" onClick={() => chooseCascade({ kind: "seguir_igual" })} data-testid="opp-cascade-seguir_igual"
                    className="rounded-md border border-brand-line px-3 py-1.5 text-xs font-bold text-brand-muted hover:text-brand-ink">Seguir igual</button>
                </div>
              </div>
            )}
            <p className="text-xs text-brand-muted">Oportunidades preparadas · {candidates.length} {candidates.length === 1 ? "candidato" : "candidatos"}. Separamos siempre <span className="font-semibold text-brand-ink">dónde se encontró</span> de <span className="font-semibold text-brand-ink">dónde recomendamos contactar</span>.</p>
            {candidates.map((c, i) => renderCard(c, i, "new"))}
            {job.completando && (
              <p className="inline-flex items-center gap-2 text-xs text-brand-muted" data-testid="opp-completando">
                <Loader2 size={13} className="animate-spin text-brand-accent" /> Seguimos buscando más para ti. Si encontramos otras, aparecerán aquí.
              </p>
            )}
            {job.reserved_count > 0 && (
              <button type="button" onClick={loadMore} disabled={loadingMore} data-testid="opp-load-more" className="inline-flex items-center gap-1.5 rounded-md border border-brand-accent/40 px-3 py-1.5 text-xs font-bold text-brand-accent hover:bg-brand-accent/[0.06] disabled:opacity-60">
                {loadingMore ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Ver más oportunidades ({job.reserved_count})
              </button>
            )}
          </section>
        )}

        {job && job.status === "preparadas" && candidates.length === 0 && (
          <div className="rounded-lg border border-brand-line bg-brand-panel p-6 text-center" data-testid="opp-all-handled">
            <CheckCircle2 size={22} className="mx-auto mb-2 text-brand-pos" />
            <p className="text-sm font-semibold text-brand-ink">Has revisado todos los candidatos de esta búsqueda.</p>
            <p className="mt-1 text-xs text-brand-muted">Cuando quieras, lanza una nueva búsqueda desde el formulario superior.</p>
          </div>
        )}

        {pendingView.length > 0 && (
          <section ref={pendingRef} className="mt-6 space-y-3" data-testid="opp-pending">
            <div>
              <p className="text-sm font-bold text-brand-ink">Pendientes de revisar · {pendingView.length}</p>
              <p className="text-xs text-brand-muted">De búsquedas anteriores. Siguen aquí hasta que las guardes en contactos o las descartes.</p>
            </div>
            {pendingView.map((c, i) => renderCard(c, i, "pending"))}
          </section>
        )}

        {!job && (
          <div className="rounded-lg border border-dashed border-brand-line bg-brand-panel p-8 text-center" data-testid="opp-idle">
            <Search size={22} className="mx-auto mb-2 text-brand-muted/60" />
            <p className="text-sm font-semibold text-brand-ink">Aún no has lanzado ninguna búsqueda</p>
            <p className="mx-auto mt-1 max-w-md text-xs text-brand-muted">Elige un avatar y un objetivo, revisa las palabras propuestas y pulsa «Encontrar oportunidades».</p>
          </div>
        )}
      </div>
    </div>
  );
}
